/**
 * Was eine Empfängergruppe ausmacht — für Suche und Filter: Art, Sportschule
 * und Altersspanne. Das Alter kommt aus der Filterdefinition der Gruppe
 * (age_between), sonst aus dem Namen („6–13 Jahre", „unter 6", „ab 14").
 */
import type { Segment } from '../../lib/supabase';
import { SPORTSCHULEN } from '../../lib/sportschulen';

export type GruppenArt = 'mitglieder' | 'familien' | 'nicht-zahlaktiv' | 'tag' | 'ehemalige' | 'intern' | 'sonstige';

export const ARTEN: { id: GruppenArt; label: string; text: string }[] = [
  { id: 'mitglieder', label: 'Mitglieder', text: 'Mitglieder einer Sportschule oder aller, nach Zahlstatus' },
  { id: 'familien', label: 'Familien & Kinder', text: 'Eltern-Postfächer, nach Alter der Kinder' },
  { id: 'nicht-zahlaktiv', label: 'Nicht zahlaktiv', text: 'Laufender Vertrag, zahlt gerade nicht' },
  { id: 'tag', label: 'CRM-Tags', text: 'Alle mit einem Tag im CRM, z. B. Kader' },
  { id: 'ehemalige', label: 'Ehemalige', text: 'Frühere Mitglieder' },
  { id: 'sonstige', label: 'Sonstige', text: 'Trainer, Events, Sonderlisten' },
  { id: 'intern', label: 'Interne Verteiler', text: 'Bekommen jede Mail in Kopie' },
];

export const ALTER_MAX = 99;

export interface GruppenInfo {
  art: GruppenArt;
  /** Sportschulen (Namen wie im CRM, z. B. „Wilmersdorf"); leer = übergreifend */
  schulen: string[];
  /** Altersspanne der Personen bzw. Kinder, falls bekannt */
  alter: [number, number] | null;
}

function altersFilter(node: unknown, out: [number, number][]): void {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) { node.forEach((n) => altersFilter(n, out)); return; }
  const o = node as Record<string, unknown>;
  if (o.op === 'age_between' && Array.isArray(o.value) && o.value.length === 2) {
    const [a, b] = o.value.map(Number);
    if (Number.isFinite(a) && Number.isFinite(b)) out.push([Math.max(0, a), Math.min(ALTER_MAX, b)]);
  }
  Object.values(o).forEach((v) => altersFilter(v, out));
}

function alterAusName(name: string): [number, number] | null {
  const n = name.toLowerCase();
  let m = /(\d{1,2})\s*[–-]\s*(\d{1,3})\s*jahr/.exec(n);
  if (m) return [Number(m[1]), Math.min(ALTER_MAX, Number(m[2]))];
  m = /unter\s+(\d{1,2})/.exec(n);
  if (m) return [0, Number(m[1]) - 1];
  m = /ab\s+(\d{1,2})\s*jahr/.exec(n) ?? /(\d{1,2})\s+und\s+älter/.exec(n);
  if (m) return [Number(m[1]), ALTER_MAX];
  return null;
}

export function gruppenInfo(g: Segment): GruppenInfo {
  const n = g.name.toLowerCase();
  const orgs = g.studio_scope ?? [];
  const schulen = SPORTSCHULEN.filter((s) => s.orgs.some((o) => orgs.includes(o))).map((s) => s.name);

  const bereiche: [number, number][] = [];
  altersFilter(g.filter_query, bereiche);
  const alter: [number, number] | null = bereiche.length
    ? [Math.min(...bereiche.map((b) => b[0])), Math.max(...bereiche.map((b) => b[1]))]
    : alterAusName(g.name);

  let art: GruppenArt;
  if (g.always_included) art = 'intern';
  else if (g.crm_tag_slug) art = 'tag';
  else if (n.startsWith('ehemalige')) art = 'ehemalige';
  else if (n.startsWith('nicht zahlaktiv')) art = 'nicht-zahlaktiv';
  else if (/familie|eltern|kinder|kids/.test(n) || JSON.stringify(g.filter_query ?? {}).includes('household_')) art = 'familien';
  else if (/mitglied|zahlaktiv/.test(n) || SPORTSCHULEN.some((s) => n === s.name.toLowerCase())) art = 'mitglieder';
  else art = 'sonstige';

  return { art, schulen, alter };
}

export interface FilterWert {
  suche: string;
  /** Gewählte Sportschulen; leer = alle */
  schulen: string[];
  /** Gruppen ohne Sportschule (übergreifend) zeigen */
  uebergreifend: boolean;
  /** Gewählte Arten; leer = alle */
  arten: GruppenArt[];
  /** Nur Gruppen, deren Alter sich mit dieser Spanne überschneidet */
  alter: [number, number] | null;
}

export const LEERER_FILTER: FilterWert = { suche: '', schulen: [], uebergreifend: true, arten: [], alter: null };

export function istGefiltert(f: FilterWert, standard: FilterWert = LEERER_FILTER): boolean {
  return f.suche.trim() !== '' || f.alter !== null || f.arten.join() !== standard.arten.join()
    || f.schulen.join() !== standard.schulen.join() || f.uebergreifend !== standard.uebergreifend;
}

export function passt(g: Segment, f: FilterWert, info: GruppenInfo = gruppenInfo(g)): boolean {
  const q = f.suche.trim().toLowerCase();
  if (q && !`${g.name} ${g.description ?? ''}`.toLowerCase().includes(q)) return false;
  if (f.arten.length && !f.arten.includes(info.art)) return false;
  if (info.schulen.length === 0) {
    if (!f.uebergreifend) return false;
  } else if (f.schulen.length && !info.schulen.some((s) => f.schulen.includes(s))) {
    return false;
  }
  if (f.alter) {
    if (!info.alter) return false;
    if (info.alter[1] < f.alter[0] || info.alter[0] > f.alter[1]) return false;
  }
  return true;
}

export function alterText(a: [number, number]): string {
  if (a[0] <= 0 && a[1] >= ALTER_MAX) return 'jedes Alter';
  if (a[1] >= ALTER_MAX) return `ab ${a[0]} Jahren`;
  if (a[0] <= 0) return `bis ${a[1]} Jahre`;
  return `${a[0]}–${a[1]} Jahre`;
}
